/**
 * NBDY product accessory — variant picker + CTA state machine.
 *
 * Responsibilities:
 *   1. Variant picker (color × size) — clicks on swatches / size pills resolve
 *      to a matching variant, write its id into the form, swap the active
 *      gallery image, update the price, toggle sold-out state.
 *   2. Mirrored inline + sticky controls — clicks on either set propagate to
 *      both because every option button shares the same data-option-position
 *      / data-option-value attributes (we query them all on each interaction).
 *   3. CTA state machine — ready → loading → added → ready. Hooks into
 *      Horizon's pipeline by listening to the form's `submit` event for
 *      loading and the global `cart:update` event for success.
 *   4. Cart drawer integration — Horizon's `<cart-drawer>` auto-opens on
 *      `cart:update`, so we don't dispatch anything. The 1.8 s success-state
 *      window matches the drawer's slide-in cadence.
 */
import { formatMoney } from '@theme/money-formatting';

const STATE_RESET_DELAY_MS = 1800;
const WIZARD_INACTIVITY_MS = 3000;
const REVEAL_EASING = 'cubic-bezier(0.34, 1.56, 0.64, 1)'; // gentle back-easing

class NbdyAccessoryPicker extends HTMLElement {
  connectedCallback() {
    this.variants = this.#parseVariantData();
    if (!this.variants) return;

    this.form = document.getElementById(this.dataset.form);
    this.variantInput = this.form?.querySelector('input[name="id"]') ?? null;

    this.optionStates = Array.from(this.querySelectorAll('[data-option-state]'));
    this.priceEls = Array.from(this.querySelectorAll('[data-accessory-price]'));
    this.memberPriceEls = Array.from(this.querySelectorAll('[data-accessory-member-price]'));
    this.memberMultiplier = Number(this.dataset.memberMultiplier) || 0;
    this.memberDiscountPct = Number(this.dataset.memberDiscountPct) || 0;
    this.memberActive = this.dataset.memberActive === 'true';
    // Shop's money format + active currency, so JS-rendered prices match the
    // server-rendered `| money` output (currency symbol + separators).
    this.moneyFormat = this.dataset.moneyFormat || '';
    this.currencyCode = this.dataset.currency || '';
    this.colorValueEl = this.querySelector('[data-color-value]');
    this.galleryImgs = Array.from(this.querySelectorAll('.nbdy-product-accessory__stage-img[data-variant-id]'));
    this.thumbs = Array.from(this.querySelectorAll('.nbdy-product-accessory__thumb'));
    this.mobileBar = this.querySelector('[data-mobile-bar]');

    // Mobile carousel refs. On mobile the stage is a horizontal scroll-snap
    // track; we listen to scroll → update progress bar + sync the active
    // variant (bidirectional with color swatches). On desktop the stage is
    // a static stacked-image container so these listeners are inert.
    this.stage = this.querySelector('[data-stage]');
    this.progressFill = this.querySelector('[data-progress-fill]');
    this.suppressGalleryScroll = false;
    this.lastScrolledVariantId = null;

    // Every CTA in the section — inline buy column + sticky bar — gets the
    // same state class swaps so the customer sees consistent feedback wherever
    // they are scrolled.
    this.ctas = Array.from(this.querySelectorAll('[data-add-cta]'));

    this.addLabel = this.dataset.addLabel || 'Add to bag';
    this.soldOutLabel = this.dataset.soldOutLabel || 'Sold out';
    this.addingLabel = this.dataset.addingLabel || 'Adding…';
    this.addedLabel = this.dataset.addedLabel || 'Added to bag';

    this.state = 'ready';
    this.expectingCartUpdate = false;
    this.resetTimer = null;

    // Custom dropdowns (desktop right column + mobile sticky-bar variant row).
    this.selects = Array.from(this.querySelectorAll('[data-select]'));

    this.addEventListener('click', this.#handleClick);
    document.addEventListener('click', this.#handleOutsideClick);
    document.addEventListener('keydown', this.#handleSelectKeydown);

    if (this.form) {
      this.form.addEventListener('submit', this.#handleSubmit);
    }
    document.addEventListener('cart:update', this.#handleCartUpdate);
    document.addEventListener('cart:error', this.#handleCartError);

    const trigger = this.querySelector('[data-size-guide-trigger]');
    if (trigger) {
      trigger.addEventListener('click', this.#openSizingGuide);
    }

    if ('IntersectionObserver' in window && this.mobileBar) {
      this.#setupMobileBar();
    }

    if (this.stage && this.galleryImgs.length > 1) {
      this.stage.addEventListener('scroll', this.#handleStageScroll, { passive: true });
      // Initial paint of progress bar + thumbs overflow detection.
      requestAnimationFrame(() => {
        this.#handleStageScroll();
        this.#updateThumbsOverflow();
      });
    }

    // Thumbs scroll container — toggle data-overflow for edge-fade mask.
    const thumbsContainer = this.querySelector('.nbdy-product-accessory__thumbs');
    if (thumbsContainer) {
      this.thumbsContainer = thumbsContainer;
      window.addEventListener('resize', this.#updateThumbsOverflow);
    }

    // Stacked gallery (gallery_layout="stacked"): mobile swipe carousel with a
    // dots indicator on mobile + a click-to-zoom lightbox on desktop.
    this.#setupStackedGallery();
    this.#setupZoom();

    // Size the section's bottom padding to the sticky bar so the last content
    // clears it without an oversized white gap. Recomputed on resize.
    this.section = this.closest('.nbdy-product-accessory');
    requestAnimationFrame(this.#syncMobileBarSpace);
    window.addEventListener('resize', this.#syncMobileBarSpace);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    document.removeEventListener('click', this.#handleOutsideClick);
    document.removeEventListener('keydown', this.#handleSelectKeydown);
    this.form?.removeEventListener('submit', this.#handleSubmit);
    document.removeEventListener('cart:update', this.#handleCartUpdate);
    document.removeEventListener('cart:error', this.#handleCartError);
    this.stage?.removeEventListener('scroll', this.#handleStageScroll);
    this.stackedTrack?.removeEventListener('scroll', this.#handleStackedScroll);
    window.removeEventListener('resize', this.#updateThumbsOverflow);
    window.removeEventListener('resize', this.#syncMobileBarSpace);
    this.observer?.disconnect();
    if (this.resetTimer) clearTimeout(this.resetTimer);
  }

  #parseVariantData() {
    try {
      return JSON.parse(this.dataset.variantData || '[]');
    } catch (_err) {
      return null;
    }
  }

  // ---------- Variant resolution ----------

  #handleClick = (event) => {
    // ---- Zoom lightbox (stacked gallery) ----
    // The corner badge is the explicit zoom trigger everywhere; on desktop the
    // whole image is also clickable. On mobile only the badge zooms, so swiping
    // the carousel never opens the lightbox by accident.
    const zoomBadge = event.target.closest('[data-zoom-trigger]');
    if (zoomBadge) {
      event.preventDefault();
      this.#openZoomFromSlide(zoomBadge.closest('[data-stacked-slide]'));
      return;
    }
    const zoomSlide = event.target.closest('[data-stacked-slide]');
    if (zoomSlide && window.innerWidth > 1100) {
      this.#openZoomFromSlide(zoomSlide);
      return;
    }

    // ---- Custom variant dropdowns (desktop) ----
    const selectTrigger = event.target.closest('[data-select-trigger]');
    if (selectTrigger) {
      event.preventDefault();
      this.#toggleSelect(selectTrigger.closest('[data-select]'));
      return;
    }
    const selectOption = event.target.closest('.nbdy-product-accessory__select-option');
    if (selectOption) {
      event.preventDefault();
      if (selectOption.classList.contains('is-unavailable')) return;
      this.#selectOption(selectOption);
      this.#closeAllSelects();
      this.#resolveVariant();
      return;
    }

    // ---- Spotlight-gallery thumbnail (variant-driven gallery only) ----
    const thumbBtn = event.target.closest('.nbdy-product-accessory__thumb');
    if (thumbBtn) {
      this.#syncFromThumb(thumbBtn);
      this.#resolveVariant();
    }
  };

  // Takes a dropdown <li option>. Keeps every option list + every trigger
  // (desktop right column AND mobile sticky bar share the same option positions)
  // and the hidden form input in sync.
  #selectOption(el) {
    const position = el.dataset.optionPosition;
    const value = el.dataset.optionValue;
    if (!position || !value) return;
    const posEsc = this.#cssEscape(position);

    // All option rows for this position (across every dropdown instance).
    this.querySelectorAll(`.nbdy-product-accessory__select-option[data-option-position="${posEsc}"]`).forEach((opt) => {
      const isSel = opt.dataset.optionValue === value;
      opt.classList.toggle('is-selected', isSel);
      opt.setAttribute('aria-selected', String(isSel));
    });

    // All triggers for this position (desktop + sticky bar) — update value + swatch.
    const sw =
      el.querySelector('.nbdy-product-accessory__select-swatch')?.getAttribute('style') ||
      el.dataset.swatch ||
      '';
    this.querySelectorAll(`[data-select][data-option-position="${posEsc}"]`).forEach((select) => {
      const valueEl = select.querySelector('[data-select-value]');
      if (valueEl) valueEl.textContent = el.dataset.optionLabel || value;
      const swatchEl = select.querySelector('[data-select-swatch]');
      if (swatchEl && sw) swatchEl.setAttribute('style', sw);
    });

    const stateInput = this.optionStates.find((e) => e.dataset.optionPosition === position);
    if (stateInput) stateInput.value = value;
  }

  // ---------- Custom dropdowns ----------

  #toggleSelect(select) {
    if (!select) return;
    const isOpen = select.dataset.open === 'true';
    this.#closeAllSelects();
    if (!isOpen) {
      select.dataset.open = 'true';
      const list = select.querySelector('[data-select-list]');
      const trigger = select.querySelector('[data-select-trigger]');
      if (list) list.hidden = false;
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
    }
  }

  #closeAllSelects() {
    this.selects?.forEach((s) => {
      s.dataset.open = 'false';
      const list = s.querySelector('[data-select-list]');
      const trigger = s.querySelector('[data-select-trigger]');
      if (list) list.hidden = true;
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
    });
  }

  #handleOutsideClick = (event) => {
    if (!this.selects?.length) return;
    if (event.target.closest('[data-select]')) return;
    this.#closeAllSelects();
  };

  #handleSelectKeydown = (event) => {
    if (event.key !== 'Escape') return;
    if (this.selects?.some((s) => s.dataset.open === 'true')) this.#closeAllSelects();
  };

  #syncFromThumb(thumb) {
    const variantId = thumb.dataset.variantId;
    if (!variantId) return;
    const variant = this.variants.find((v) => String(v.id) === String(variantId));
    if (!variant) return;

    variant.options.forEach((value, idx) => {
      const position = String(idx + 1);
      const matchBtn = this.querySelector(
        `button[data-option-position="${position}"][data-option-value="${this.#cssEscape(value)}"]`
      );
      if (matchBtn) this.#selectOption(matchBtn);
      if (matchBtn?.classList.contains('nbdy-product-accessory__color') && this.colorValueEl) {
        this.colorValueEl.textContent = matchBtn.dataset.optionLabel || value;
      }
    });
  }

  #resolveVariant() {
    const selected = this.optionStates.map((el) => el.value);
    const variant = this.variants.find((v) =>
      v.options.every((value, idx) => value === selected[idx])
    );

    if (!variant) {
      this.currentVariant = null;
      this.#setReadyState({ available: false, label: this.soldOutLabel });
      return;
    }

    this.currentVariant = variant;
    if (this.variantInput) this.variantInput.value = String(variant.id);

    const regular = this.#formatMoney(variant.price);
    const memberCents = Math.round((variant.price * this.memberMultiplier) / 100);
    const member = this.#formatMoney(memberCents);

    // Main price: logged-in members see the member price; everyone else the
    // regular price.
    this.priceEls.forEach((el) => {
      const value = this.memberActive ? member : regular;
      if (value) el.textContent = value;
    });

    // Signed-out teaser's member price ("Sign in to purchase @ <member>").
    if (this.memberDiscountPct > 0 && this.memberPriceEls.length > 0) {
      this.memberPriceEls.forEach((el) => {
        if (member) el.textContent = member;
      });
    }

    this.galleryImgs.forEach((img) => {
      const matches = String(img.dataset.variantId) === String(variant.id);
      img.classList.toggle('is-active', matches);
    });
    this.thumbs.forEach((thumb) => {
      const matches = String(thumb.dataset.variantId) === String(variant.id);
      thumb.classList.toggle('is-active', matches);
    });

    // Bidirectional sync: on mobile the stage is a horizontal carousel —
    // scroll it to the variant's slide. suppressGalleryScroll is true when
    // the variant change ORIGINATED from a scroll (avoids infinite loop).
    if (this.stage && !this.suppressGalleryScroll && getComputedStyle(this.stage).overflowX !== 'hidden') {
      const targetImg = this.galleryImgs.find((img) => String(img.dataset.variantId) === String(variant.id));
      if (targetImg) {
        this.stage.scrollTo({ left: targetImg.offsetLeft, behavior: 'smooth' });
      }
    }

    this.#setReadyState({
      available: variant.available,
      label: variant.available ? this.addLabel : this.soldOutLabel,
    });
  }

  // ---------- Mobile carousel ----------

  #handleStageScroll = () => {
    if (!this.stage) return;

    // Progress bar — scale 0 → 1 based on horizontal scroll fraction.
    const scrollMax = this.stage.scrollWidth - this.stage.clientWidth;
    const progress = scrollMax > 0 ? this.stage.scrollLeft / scrollMax : 0;
    if (this.progressFill) {
      this.progressFill.style.transform = `scaleX(${progress})`;
    }

    // Find the slide nearest the horizontal center of the viewport. That
    // becomes the "active" variant in the form / pickers.
    const viewportCenter = this.stage.scrollLeft + this.stage.clientWidth / 2;
    let closest = null;
    let closestDist = Infinity;
    for (const img of this.galleryImgs) {
      const imgCenter = img.offsetLeft + img.offsetWidth / 2;
      const dist = Math.abs(imgCenter - viewportCenter);
      if (dist < closestDist) {
        closestDist = dist;
        closest = img;
      }
    }

    if (!closest) return;
    const variantId = closest.dataset.variantId;
    if (!variantId || variantId === this.lastScrolledVariantId) return;
    this.lastScrolledVariantId = variantId;

    // Mirror the scroll-derived selection into the option state inputs +
    // pickers, but DON'T re-scroll the carousel (would create a loop).
    this.suppressGalleryScroll = true;
    this.#syncFromThumb({ dataset: { variantId } });
    this.#resolveVariant();
    this.suppressGalleryScroll = false;
  };

  #updateThumbsOverflow = () => {
    if (!this.thumbsContainer) return;
    const hasOverflow =
      this.thumbsContainer.scrollWidth > this.thumbsContainer.clientWidth + 1 ||
      this.thumbsContainer.scrollHeight > this.thumbsContainer.clientHeight + 1;
    this.thumbsContainer.dataset.overflow = String(hasOverflow);
  };

  // Match the section's bottom padding to the sticky bar height (mobile only),
  // so the last content clears the bar with a small, consistent gap instead of
  // a large fixed white space.
  #syncMobileBarSpace = () => {
    if (!this.section || !this.mobileBar) return;
    if (window.innerWidth <= 1100) {
      this.section.style.paddingBottom = `${this.mobileBar.offsetHeight + 16}px`;
    } else {
      this.section.style.paddingBottom = '';
    }
  };

  // ---------- Stacked gallery (mobile carousel) ----------

  #setupStackedGallery() {
    this.stackedTrack = this.querySelector('[data-stacked-track]');
    this.stackedSlides = Array.from(this.querySelectorAll('[data-stacked-slide]'));
    this.dotsContainer = this.querySelector('[data-stacked-dots]');
    if (!this.stackedTrack || !this.dotsContainer) return;

    // One dot per slide (mobile only — hidden by CSS on desktop). A single
    // image needs no carousel UI.
    if (this.stackedSlides.length > 1) {
      this.dots = this.stackedSlides.map((_, i) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'nbdy-product-accessory__dot' + (i === 0 ? ' is-active' : '');
        dot.setAttribute('aria-label', `Go to image ${i + 1}`);
        dot.addEventListener('click', () => this.#scrollToSlide(i));
        this.dotsContainer.appendChild(dot);
        return dot;
      });
      this.stackedTrack.addEventListener('scroll', this.#handleStackedScroll, { passive: true });
    } else {
      this.dotsContainer.dataset.single = 'true';
    }
  }

  #scrollToSlide(index) {
    const slide = this.stackedSlides?.[index];
    if (slide && this.stackedTrack) {
      this.stackedTrack.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
    }
  }

  #handleStackedScroll = () => {
    if (!this.stackedTrack || !this.dots) return;
    const center = this.stackedTrack.scrollLeft + this.stackedTrack.clientWidth / 2;
    let active = 0;
    let closest = Infinity;
    this.stackedSlides.forEach((slide, i) => {
      const slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
      const dist = Math.abs(slideCenter - center);
      if (dist < closest) {
        closest = dist;
        active = i;
      }
    });
    this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === active));
  };

  // ---------- Zoom lightbox ----------

  #setupZoom() {
    this.zoomDialog = this.querySelector('[data-zoom-dialog]');
    this.zoomStage = this.querySelector('[data-zoom-stage]');
    if (!this.zoomDialog || !this.zoomStage) return;

    // The <img> is created here so the markup never ships a static empty src.
    this.zoomImg = document.createElement('img');
    this.zoomImg.className = 'nbdy-product-accessory__zoom-img';
    this.zoomImg.alt = '';
    this.zoomStage.appendChild(this.zoomImg);

    // Close on the X button, or on a click in the empty stage area around the
    // image (the backdrop). Clicking the image itself does nothing so mobile
    // pinch-zoom still works.
    this.zoomDialog.addEventListener('click', (event) => {
      if (
        event.target.closest('[data-zoom-close]') ||
        event.target === this.zoomDialog ||
        event.target === this.zoomStage
      ) {
        this.#closeZoom();
      }
    });
    // Reset pan position whenever the dialog closes (incl. native Esc).
    this.zoomDialog.addEventListener('close', () => {
      this.zoomStage.scrollTo(0, 0);
    });
  }

  #openZoomFromSlide(slide) {
    if (!slide || !this.zoomDialog || !this.zoomImg) return;
    const src = slide.dataset.zoomSrc;
    if (!src) return;
    this.zoomImg.src = src;
    this.zoomImg.alt = slide.querySelector('img')?.alt || '';
    if (typeof this.zoomDialog.showModal === 'function') this.zoomDialog.showModal();
  }

  #closeZoom() {
    if (this.zoomDialog?.open) this.zoomDialog.close();
  }

  // ---------- CTA state machine ----------

  #handleSubmit = () => {
    // Only respond when we have a valid available variant — otherwise let the
    // form's native validation / Horizon's error handling take over.
    if (this.currentVariant && this.currentVariant.available === false) return;
    this.expectingCartUpdate = true;
    this.#setLoadingState();
  };

  #handleCartUpdate = () => {
    if (!this.expectingCartUpdate) return;
    this.expectingCartUpdate = false;
    this.#setAddedState();

    if (this.resetTimer) clearTimeout(this.resetTimer);
    this.resetTimer = setTimeout(() => {
      this.#setReadyState({
        available: this.currentVariant?.available ?? true,
        label: this.currentVariant?.available !== false ? this.addLabel : this.soldOutLabel,
      });
    }, STATE_RESET_DELAY_MS);
  };

  #handleCartError = () => {
    if (!this.expectingCartUpdate) return;
    this.expectingCartUpdate = false;
    // Snap back to ready so the customer can retry.
    this.#setReadyState({
      available: this.currentVariant?.available ?? true,
      label: this.currentVariant?.available !== false ? this.addLabel : this.soldOutLabel,
    });
  };

  #setReadyState({ available, label }) {
    this.state = 'ready';
    this.ctas.forEach((cta) => {
      cta.classList.remove('is-loading', 'is-added');
      cta.disabled = !available;
      const labelEl = cta.querySelector('[data-cta-label]');
      if (labelEl) labelEl.textContent = label;
    });
  }

  #setLoadingState() {
    this.state = 'loading';
    this.ctas.forEach((cta) => {
      cta.classList.add('is-loading');
      cta.classList.remove('is-added');
      cta.disabled = true;
      const labelEl = cta.querySelector('[data-cta-label]');
      if (labelEl) labelEl.textContent = this.addingLabel;
      // Subtle pulse on the label so it doesn't feel inert.
      this.#animate(labelEl, [{ opacity: 0.5 }, { opacity: 1 }], {
        duration: 320,
        easing: 'ease-in-out',
      });
    });
  }

  #setAddedState() {
    this.state = 'added';
    this.ctas.forEach((cta) => {
      cta.classList.remove('is-loading');
      cta.classList.add('is-added');
      cta.disabled = true;
      const labelEl = cta.querySelector('[data-cta-label]');
      if (labelEl) labelEl.textContent = this.addedLabel;

      // Checkmark draw-in + label slide-up.
      const check = cta.querySelector('[data-cta-check]');
      if (check) {
        this.#animate(
          check,
          [
            { opacity: 0, transform: 'scale(0.6)' },
            { opacity: 1, transform: 'scale(1)' },
          ],
          { duration: 320, easing: REVEAL_EASING }
        );
      }
      this.#animate(
        labelEl,
        [
          { opacity: 0, transform: 'translateY(6px)' },
          { opacity: 1, transform: 'translateY(0)' },
        ],
        { duration: 280, easing: REVEAL_EASING }
      );
    });
  }

  // ---------- Helpers ----------

  #animate(el, keyframes, options) {
    if (!el || typeof el.animate !== 'function') return;
    el.animate(keyframes, { ...options, fill: 'forwards' });
  }

  #formatMoney(cents) {
    if (typeof cents !== 'number') return '';
    if (this.moneyFormat) {
      try {
        return formatMoney(cents, this.moneyFormat, this.currencyCode || 'USD');
      } catch (_err) {
        /* fall through */
      }
    }
    return `${(cents / 100).toFixed(2)}`;
  }

  #cssEscape(value) {
    if (window.CSS?.escape) return window.CSS.escape(value);
    return String(value).replace(/(["\\])/g, '\\$1');
  }

  #openSizingGuide = (event) => {
    event.preventDefault();
    const guide = this.querySelector('[data-sizing-guide]');
    if (!guide) return;
    guide.open = true;
    if (window.innerWidth <= 1100) {
      guide.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  #setupMobileBar() {
    // The sticky bar is the only add-to-cart entry point on mobile (the
    // inline buy-column CTA is hidden via CSS at <1101px). So we always want
    // it visible on mobile. The IntersectionObserver toggle only makes sense
    // if the inline CTA is currently visible (i.e. desktop in some custom
    // theme override) — skip the observer otherwise.
    const inlineCta = this.querySelector('.nbdy-product-accessory__cta:not(.nbdy-product-accessory__mobile-bar-cta)');
    if (!inlineCta) return;
    if (getComputedStyle(inlineCta).display === 'none') return;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          this.mobileBar.dataset.hidden = String(entry.isIntersecting);
        });
      },
      { threshold: 0.01, rootMargin: '0px 0px -80px 0px' }
    );
    this.observer.observe(inlineCta);
  }
}

if (!customElements.get('nbdy-accessory-picker')) {
  customElements.define('nbdy-accessory-picker', NbdyAccessoryPicker);
}
